/**
 * #206 live work in progress: the "Live: Travis since 4:51 PM" chip (case and queue row), "I'm on it now" and
 * "Done for now". Owner: #205/#206 (web half).
 */
import { Button, Chip } from '@patchbay/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Radio } from 'lucide-react'
import * as React from 'react'
import {
  type CaseLiveDto,
  caseLiveQuery,
  type EndOutcome,
  errorText,
  type LiveWorkDto,
  liveAllQuery,
  liveApi,
  liveLabel,
  type RemoteWorkCard,
} from './api'
import { outcomeLabel, RemoteWorkCardView } from './cards'

export { LiveSuggestionChip, useMyLive, WorkItNowButton, WorkTimer } from './timer'

export function LiveChip({ rows }: { rows: Pick<LiveWorkDto, 'who' | 'started_at'>[] }) {
  if (!rows.length) return null
  const text = liveLabel(rows)
  return (
    <Chip tone="success" title={text} data-slot="live-chip">
      <Radio aria-hidden /> {text}
    </Chip>
  )
}

/** The case header's chip (#206): who is live on this case. Silent until the read answers. */
export function CaseLiveChip({ caseId }: { caseId: string }) {
  const live = useCaseLive(caseId)
  return <LiveChip rows={live.data?.live ?? []} />
}

/** The queue row's chip: one GET /live for the whole queue, joined on the row's case id. Silent on any failure. */
export function QueueLiveChip({ caseId }: { caseId: string }) {
  const q = useQuery(liveAllQuery())
  const rows = React.useMemo(() => (q.data ?? []).filter((r) => r.case_id === caseId), [q.data, caseId])
  return <LiveChip rows={rows} />
}

/** "I'm on it now": freezes a `go_live` card (Desk status to working, you as owner) and confirms it here. */
export function OnItNowButton({
  caseId,
  onCard,
  trigger = 0,
  hideButton = false,
}: {
  caseId: string
  onCard?: (card: RemoteWorkCard) => void
  /** Bumped by the case's ⋯ menu "I'm on it now": proposes the card here. */
  trigger?: number
  /** The ⋯ menu is the entry point: show only the card (or an error) it produced. */
  hideButton?: boolean
}) {
  const [card, setCard] = React.useState<RemoteWorkCard | null>(null)
  const propose = useMutation({
    mutationFn: () => liveApi.proposeGoLive(caseId),
    onSuccess: (c) => {
      setCard(c)
      onCard?.(c)
    },
  })
  const { mutate } = propose
  React.useEffect(() => {
    if (trigger > 0) mutate()
  }, [trigger, mutate])
  if (card) {
    return (
      <div className="w-full">
        <RemoteWorkCardView card={card} onDecided={(c) => c.state !== 'proposed' && setCard(null)} />
      </div>
    )
  }
  if (hideButton && !propose.isError) return null
  return (
    <span className="inline-flex items-center gap-2">
      {hideButton ? null : (
        <Button size="sm" onClick={() => propose.mutate()} disabled={propose.isPending}>
          I'm on it now
        </Button>
      )}
      {propose.isError ? (
        <span role="alert" className="text-xs text-destructive">
          {errorText(propose.error)}
        </span>
      ) : null}
    </span>
  )
}

const OUTCOME_ORDER: EndOutcome[] = ['waiting', 'resolved', 'keep']

/** "Done for now": pick the next status, then confirm the frozen `end_live` card. */
export function DoneForNow({ caseId, live, onCard }: { caseId: string; live: CaseLiveDto; onCard?: (card: RemoteWorkCard) => void }) {
  const [open, setOpen] = React.useState(false)
  const [card, setCard] = React.useState<RemoteWorkCard | null>(null)
  const propose = useMutation({
    mutationFn: (outcome: EndOutcome) => liveApi.proposeEnd(caseId, outcome),
    onSuccess: (c) => {
      setCard(c)
      setOpen(false)
      onCard?.(c)
    },
  })
  const status: Record<EndOutcome, string | null | undefined> = {
    waiting: live.waiting_status,
    resolved: live.resolved_status,
    keep: live.desk_status,
  }
  if (card) {
    return (
      <div className="w-full">
        <RemoteWorkCardView card={card} pickOutcome={false} onDecided={(c) => c.state !== 'proposed' && setCard(null)} />
      </div>
    )
  }
  // #1013: the end control never stands alone; it always sits next to the live chip that says who is live since when.
  if (!live.live.length) return null
  if (!open) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-slot="done-for-now">
        <LiveChip rows={live.live} />
        <Button size="sm" title="Stop live work and log the time" onClick={() => setOpen(true)}>
          Done for now
        </Button>
      </span>
    )
  }
  return (
    <fieldset className="flex flex-wrap items-center gap-2" aria-label="Next status">
      <legend className="sr-only">Next status</legend>
      <span className="text-xs text-muted-foreground">Next status:</span>
      {OUTCOME_ORDER.map((o) => (
        <Button key={o} size="sm" disabled={propose.isPending} onClick={() => propose.mutate(o)}>
          {outcomeLabel(o, status[o])}
        </Button>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {propose.isError ? (
        <span role="alert" className="text-xs text-destructive">
          {errorText(propose.error)}
        </span>
      ) : null}
    </fieldset>
  )
}

/** The case's live state: GET /cases/{id}/live (no error surface: a failed read only hides the controls). */
export function useCaseLive(caseId: string) {
  return useQuery({ ...caseLiveQuery(caseId), enabled: Boolean(caseId) })
}
